import {getTestProps} from '@benchling/js-test-utils';
import {Chip, Input, Stack, Text, TextInput} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useRef, useState} from 'react';

import {refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {createDataFrame} from 'data-frames/network/DataFrameDAO';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {getOutputStepDatas} from 'pipelines/StepConnectionUtils';
import SearchInput from 'search/inputs/SearchInput';
import SelectableCard from 'ui-library/inputs/SelectableCard';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

enum TemplateInputType {
  VARIABLE = 'VARIABLE',
  STATIC = 'STATIC',
}

// Mantine only adds a 2px gap between a description that sits above the input and the input itself. Give the
// help text 6px of breathing room to match the design.
const FIELD_STYLES = {description: {marginBottom: 6}} as const;

/**
 * Sub-panel reached from the source step picker when a user picks "Create table from dataset" while editing an
 * analysis template. Lets the user select an existing dataset and choose whether the input is variable or
 * static, then creates a `TEMPLATE_INPUT` step whose output is a data frame copied from that dataset. The
 * backend derives a data frame variable from the step's `isVariable` config.
 */
export default function AddTemplateInputSourceRightSidebar({
  analysis,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE>): JSX.Element {
  const datasetInputId = useId();
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [inputType, setInputType] = useState<TemplateInputType>(TemplateInputType.VARIABLE);
  const [description, setDescription] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  // `isFinalizing` state doesn't update synchronously, so a fast double-click could enter `onSave` twice
  // before the rerender. This ref guards against that, so only one save runs at a time.
  const isSavingRef = useRef(false);

  const isVariable = inputType === TemplateInputType.VARIABLE;
  const canFinalize = selectedDatasetId != null && name.trim() !== '';

  const onSave = async (): Promise<void> => {
    if (!canFinalize || selectedDatasetId == null || isSavingRef.current) {
      return;
    }
    try {
      isSavingRef.current = true;
      setIsFinalizing(true);
      const trimmedName = name.trim();
      const trimmedDescription = description.trim();
      const [outputStepData] = getOutputStepDatas(PipelineStepType.TEMPLATE_INPUT);
      assert(outputStepData != null, 'TEMPLATE_INPUT step is expected to have an output data shape');
      // The backend derives the data frame variable's description from this config, so only send it for
      // variable inputs and omit it when empty.
      const config: Record<string, JSONValue> = {isVariable};
      if (isVariable && trimmedDescription !== '') {
        config.description = trimmedDescription;
      }
      await withLoadingScreen(
        (async () => {
          // The TEMPLATE_INPUT step's output is an existing persisted data frame, so copy one from the
          // selected dataset and attach it as the step's output. The backend derives a data frame variable
          // from the `isVariable` config when the step is created. These are two sequential mutations: if the
          // step creation fails, the copied data frame is left unattached (they are not atomic today).
          const {createdIds} = await createDataFrame({name: trimmedName, datasetId: selectedDatasetId});
          assert(createdIds.length === 1, 'Expected exactly one created data frame');
          await addStepWithInferredEdges(analysis, {
            stepType: PipelineStepType.TEMPLATE_INPUT,
            config,
            outputStepDatas: [{name: outputStepData.systemName, rawDataIds: [createdIds[0]]}],
          });
          await refreshAnalysisTemplate(analysis.id);
        })()
      );
      // TEMPLATE_INPUT steps are not configurable from the sidepanel, so close the sidebar rather than
      // opening the new step for editing.
      onChangeSelectedRightSidebarItem(null);
    } finally {
      setIsFinalizing(false);
      isSavingRef.current = false;
    }
  };

  return (
    <RightSidebarSkeleton
      title="Create table from dataset"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.ADD_SOURCE_STEP});
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Save',
          onClick: onSave,
          dataTestAttrsElement: 'save',
          disabled: !canFinalize || isFinalizing,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddTemplateInputSourceRightSidebar',
        element: 'root',
      }}
    >
      <Stack spacing="md">
        <div>
          <Text size="md" weight={500} mb="xs">
            Create table with:
          </Text>
          <Chip.Group
            multiple={false}
            value={inputType}
            onChange={(value) => {
              if (value) {
                setInputType(value as TemplateInputType);
              }
            }}
          >
            <Stack spacing="sm">
              <SelectableCard
                value={TemplateInputType.VARIABLE}
                title="Variable data"
                description="Dataset provided when the template is applied"
                dataTestAttrs={{
                  component: 'AddTemplateInputSourceRightSidebar',
                  element: 'variableOption',
                }}
              />
              <SelectableCard
                value={TemplateInputType.STATIC}
                title="Static data"
                description="Same dataset each time the template is applied"
                dataTestAttrs={{
                  component: 'AddTemplateInputSourceRightSidebar',
                  element: 'staticOption',
                }}
              />
            </Stack>
          </Chip.Group>
        </div>
        <Input.Wrapper
          id={datasetInputId}
          label={isVariable ? 'Reference dataset' : 'Dataset'}
          description={
            isVariable
              ? 'Used to test the analysis as you design the template. Users map to these columns when applying the template.'
              : undefined
          }
          withAsterisk={true}
          inputWrapperOrder={['label', 'description', 'input']}
          styles={FIELD_STYLES}
        >
          <SearchInput
            id={datasetInputId}
            autoFocus={false}
            disableTyping={selectedDatasetId != null}
            isMulti={false}
            onRemove={() => {
              setSelectedDatasetId(null);
            }}
            onSelect={(id) => {
              setSelectedDatasetId(id);
            }}
            placeholder=""
            selected={selectedDatasetId == null ? [] : [selectedDatasetId]}
            types={[ModelTypeConstants.DATASET]}
          />
        </Input.Wrapper>
        {isVariable && (
          <TextInput
            label="Description"
            description="Describe what data is expected so users know what to provide"
            inputWrapperOrder={['label', 'description', 'input']}
            styles={FIELD_STYLES}
            placeholder="e.g. Select a dataset with columns for..."
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            {...getTestProps({
              component: 'AddTemplateInputSourceRightSidebar',
              element: 'descriptionInput',
            })}
          />
        )}
        <TextInput
          label="Table name"
          withAsterisk={true}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          {...getTestProps({
            component: 'AddTemplateInputSourceRightSidebar',
            element: 'nameInput',
          })}
        />
      </Stack>
    </RightSidebarSkeleton>
  );
}
